import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Shield, RefreshCw, Key, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import '../styles/Login.css';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const navigate = useNavigate();
  const { login, signup, twoFARequired, verify2FA, verifyBackupCode, resend2FACode, cancel2FA } = useAuth();

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [signupData, setSignupData] = useState({
    first_name: '', last_name: '', email: '', phone: '', password: '', confirmPassword: ''
  });

  const handleLoginChange = (e) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSignupChange = (e) => {
    setSignupData({ ...signupData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    const result = await login(loginData.email, loginData.password);
    if (result.success) {
      setSuccess('Login successful! Redirecting...');
      setTimeout(() => navigate('/portal'), 1500);
    } else if (result.requires_2fa) {
      setSuccess(`Verification code sent to ${result.email_masked}`);
    } else {
      setError(result.error);
    }
    setIsLoading(false);
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    const result = await verify2FA(twoFACode);
    if (result.success) {
      setSuccess('Verification successful! Redirecting...');
      setTimeout(() => navigate('/portal'), 1500);
    } else {
      setError(result.error);
    }
    setIsLoading(false);
  };

  const handleBackupCodeSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    const result = await verifyBackupCode(backupCode);
    if (result.success) {
      setSuccess(result.warning ? `Success! ${result.warning}` : 'Verification successful!');
      setTimeout(() => navigate('/portal'), 2000);
    } else {
      setError(result.error);
    }
    setIsLoading(false);
  };

  const handleResend2FACode = async () => {
    if (resendCooldown > 0) return;
    setIsLoading(true);
    const result = await resend2FACode();
    if (result.success) {
      setSuccess(result.message);
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown((prev) => { if (prev <= 1) { clearInterval(interval); return 0; } return prev - 1; });
      }, 1000);
    } else {
      setError(result.error);
    }
    setIsLoading(false);
  };

  const handleCancel2FA = () => {
    cancel2FA();
    setTwoFACode('');
    setBackupCode('');
    setUseBackupCode(false);
    setError('');
    setSuccess('');
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    if (!agreeTerms) { setError('Please agree to the Terms and HIPAA Policy'); setIsLoading(false); return; }
    if (signupData.password !== signupData.confirmPassword) { setError('Passwords do not match'); setIsLoading(false); return; }
    if (signupData.password.length < 8) { setError('Password must be at least 8 characters'); setIsLoading(false); return; }
    const result = await signup({
      first_name: signupData.first_name, last_name: signupData.last_name,
      email: signupData.email, phone: signupData.phone, password: signupData.password
    });
    if (result.success) {
      setSuccess('Account created! Redirecting...');
      setTimeout(() => navigate('/portal'), 1500);
    } else {
      setError(result.error);
    }
    setIsLoading(false);
  };

  // 2FA View
  if (twoFARequired) {
    return (
      <div className="luxe-login-page">
        <div className="luxe-satin-bg"></div>
        <div className="luxe-card twofa-card">
          <div className="twofa-icon-wrap">
            {useBackupCode ? <Key size={48} /> : <Shield size={48} />}
          </div>
          <h2 className="twofa-title">{useBackupCode ? 'Backup Code' : 'Verification Required'}</h2>
          <p className="twofa-subtitle">{useBackupCode ? 'Enter your backup code' : 'Enter the 6-digit code sent to your email'}</p>
          
          {error && <div className="luxe-error">{error}</div>}
          {success && <div className="luxe-success"><CheckCircle size={16} /> {success}</div>}
          
          {!useBackupCode ? (
            <form onSubmit={handle2FASubmit} className="twofa-form">
              <input
                type="text"
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                className="luxe-input twofa-code-input"
                data-testid="2fa-code-input"
                required
              />
              <button type="submit" className="luxe-btn gold-btn" disabled={isLoading || twoFACode.length !== 6} data-testid="2fa-verify-btn">
                {isLoading ? 'VERIFYING...' : 'VERIFY CODE'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleBackupCodeSubmit} className="twofa-form">
              <input
                type="text"
                value={backupCode}
                onChange={(e) => setBackupCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
                placeholder="XXXX-XXXX"
                maxLength={9}
                className="luxe-input twofa-code-input"
                data-testid="backup-code-input"
                required
              />
              <button type="submit" className="luxe-btn gold-btn" disabled={isLoading || backupCode.length < 8} data-testid="backup-code-verify-btn">
                {isLoading ? 'VERIFYING...' : 'VERIFY CODE'}
              </button>
            </form>
          )}
          
          <div className="twofa-actions">
            {!useBackupCode && (
              <button onClick={handleResend2FACode} disabled={resendCooldown > 0} className="twofa-link">
                <RefreshCw size={14}/> {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
              </button>
            )}
            <button onClick={() => { setUseBackupCode(!useBackupCode); setTwoFACode(''); setBackupCode(''); }} className="twofa-link">
              <Key size={14}/> {useBackupCode ? 'Use Email Code' : 'Use Backup Code'}
            </button>
            <button onClick={handleCancel2FA} className="twofa-link">Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="luxe-login-page">
      {/* Premium Copper Satin Background */}
      <div className="luxe-satin-bg" style={{backgroundImage: `url('/copper-satin-bg.png')`}}></div>
      
      {/* Floating Messages */}
      {error && <div className="luxe-toast error-toast">{error}</div>}
      {success && <div className="luxe-toast success-toast"><CheckCircle size={16} /> {success}</div>}
      
      {/* Main Login Card - Native CSS Build */}
      <div className="luxe-card" data-testid="login-card">
        {/* Gold Shield Emblem - Clickable to Home */}
        <Link to="/" className="card-emblem" data-testid="login-emblem-link">
          <img src="/cadence-logo-gold-bg.png" alt="CADENCE - Return to Home" className="emblem-img" />
        </Link>
        
        {/* Portal Title */}
        <h1 className="card-title">CADENCE PORTAL</h1>
        
        {/* Tab Switcher */}
        <div className="card-tabs">
          <button 
            className={`tab-btn ${isLogin ? 'active' : ''}`}
            onClick={() => setIsLogin(true)}
            data-testid="signin-tab"
          >
            SIGN IN
          </button>
          <button 
            className={`tab-btn ${!isLogin ? 'active' : ''}`}
            onClick={() => setIsLogin(false)}
            data-testid="create-account-tab"
          >
            CREATE ACCOUNT
          </button>
        </div>
        
        {/* Sign In Form */}
        {isLogin ? (
          <form onSubmit={handleLoginSubmit} className="luxe-form" data-testid="signin-form">
            <div className="form-field">
              <label className="field-label">EMAIL</label>
              <input
                type="email"
                name="email"
                value={loginData.email}
                onChange={handleLoginChange}
                className="luxe-input"
                required
                data-testid="login-email-input"
                placeholder="Enter your email"
              />
            </div>
            
            <div className="form-field">
              <label className="field-label">PASSWORD</label>
              <div className="input-with-icon">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={loginData.password}
                  onChange={handleLoginChange}
                  className="luxe-input"
                  required
                  data-testid="login-password-input"
                  placeholder="Enter your password"
                />
                <button type="button" className="eye-toggle" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <button type="submit" className="luxe-btn gold-btn" disabled={isLoading} data-testid="login-submit-btn">
              {isLoading ? 'SIGNING IN...' : 'SIGN IN'}
            </button>
            
            <a href="/forgot-password" className="forgot-link" data-testid="forgot-password-link">
              Forgot Password?
            </a>
            
            {/* Continue as Guest Link */}
            <div className="guest-divider">
              <span>or</span>
            </div>
            <Link 
              to="/portal?tab=intake&guest=true" 
              className="guest-link" 
              data-testid="continue-as-guest-link"
            >
              Continue as Guest →
            </Link>
          </form>
        ) : (
          /* Create Account Form */
          <form onSubmit={handleSignupSubmit} className="luxe-form signup-form" data-testid="signup-form">
            <div className="form-field">
              <label className="field-label">FIRST NAME</label>
              <input
                type="text"
                name="first_name"
                value={signupData.first_name}
                onChange={handleSignupChange}
                className="luxe-input"
                required
                data-testid="signup-firstname-input"
              />
            </div>
            
            <div className="form-field">
              <label className="field-label">LAST NAME</label>
              <input
                type="text"
                name="last_name"
                value={signupData.last_name || ''}
                onChange={handleSignupChange}
                className="luxe-input"
                data-testid="signup-lastname-input"
              />
            </div>
            
            <div className="form-field">
              <label className="field-label">EMAIL</label>
              <input
                type="email"
                name="email"
                value={signupData.email}
                onChange={handleSignupChange}
                className="luxe-input"
                required
                data-testid="signup-email-input"
              />
            </div>
            
            <div className="form-field">
              <label className="field-label">PHONE</label>
              <input
                type="tel"
                name="phone"
                value={signupData.phone || ''}
                onChange={handleSignupChange}
                className="luxe-input"
                data-testid="signup-phone-input"
              />
            </div>
            
            <div className="form-field">
              <label className="field-label">PASSWORD</label>
              <div className="input-with-icon">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={signupData.password}
                  onChange={handleSignupChange}
                  className="luxe-input"
                  required
                  minLength={8}
                  data-testid="signup-password-input"
                />
                <button type="button" className="eye-toggle" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            
            <div className="form-field">
              <label className="field-label">CONFIRM PASSWORD</label>
              <div className="input-with-icon">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={signupData.confirmPassword}
                  onChange={handleSignupChange}
                  className="luxe-input"
                  required
                  data-testid="signup-confirm-input"
                />
                <button type="button" className="eye-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            
            <div className="terms-row">
              <input
                type="checkbox"
                id="agreeTerms"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                className="terms-checkbox"
                data-testid="agree-terms-checkbox"
              />
              <label htmlFor="agreeTerms" className="terms-label">
                I agree to the <a href="/terms">Terms of Service</a> and <a href="/hipaa">HIPAA Policy</a>
              </label>
            </div>
            
            <button type="submit" className="luxe-btn gold-btn" disabled={isLoading} data-testid="signup-submit-btn">
              {isLoading ? 'CREATING ACCOUNT...' : 'CREATE ACCOUNT'}
            </button>
          </form>
        )}
      </div>
      
      {/* Luxe Quote Section */}
      <div className="luxe-quote-section">
        {/* Main Quote - IN A WIDE TILE - 2 lines with no orphans */}
        <div className="main-quote-tile">
          <p className="main-quote">
            <span className="quote-line">"YOUR STORY DESERVES TO BE HEARD.</span>
            <span className="quote-line">YOUR HEALTH DESERVES TO BE PRIORITIZED."</span>
          </p>
        </div>
        <Link to="/" className="bottom-emblem" data-testid="login-bottom-emblem-link">
          <img src="/cadence-gold-foil.png" alt="CADENCE - Return to Home" className="bottom-emblem-img" />
        </Link>
        {/* Bottom Satin Quote - DIRECTLY ON SATIN - 4 LINES on mobile */}
        <p className="bottom-satin-quote-login">
          <span className="quote-line-1">Clarity when you need it most.</span>
          <span className="quote-line-2">Advocacy when it matters most.</span>
        </p>
        {/* Acronym Footer - Explicit line breaks for mobile */}
        <p className="login-acronym-footer">
          <span className="acronym-line">CLINICAL ADVOCACY</span>
          <span className="acronym-line">DECISIVE EMPOWERMENT</span>
          <span className="acronym-line">NAVIGATING CARE & EXPERTISE</span>
        </p>
      </div>
    </div>
  );
};

export default Login;
